import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";

export type ResourceType =
  | "Projects"
  | "Staff"
  | "Calendar"
  | "Timesheets"
  | "Buddy AI"
  | "Leave"
  | "Contacts"
  | "Admin"
  | "Dashboard"
  | "Audit"
  | "Quotes"
  | "Bills"
  | "Invoices";
export type PermissionAction =
  | "view_all"
  | "view_own"
  | "add_all"
  | "add_own"
  | "edit_all"
  | "edit_own"
  | "approve_all"
  | "approve_own"
  | "delete_all"
  | "delete_own"
  // Legacy support
  | "view"
  | "add"
  | "edit"
  | "approve"
  | "delete";

interface ResourcePermission {
  resource: ResourceType;
  actions: PermissionAction[];
}

interface PermissionsContextType {
  permissions: ResourcePermission[];
  hasPermission: (
    resource: ResourceType,
    action: PermissionAction,
  ) => boolean | "all" | "own";
  canView: (resource: ResourceType) => boolean | "all" | "own";
  canViewAll: (resource: ResourceType) => boolean;
  canViewOwn: (resource: ResourceType) => boolean;
  canAdd: (resource: ResourceType) => boolean | "all" | "own";
  canAddAll: (resource: ResourceType) => boolean;
  canAddOwn: (resource: ResourceType) => boolean;
  canEdit: (resource: ResourceType) => boolean | "all" | "own";
  canEditAll: (resource: ResourceType) => boolean;
  canEditOwn: (resource: ResourceType) => boolean;
  canApprove: (resource: ResourceType) => boolean | "all" | "own";
  canApproveAll: (resource: ResourceType) => boolean;
  canApproveOwn: (resource: ResourceType) => boolean;
  canDelete: (resource: ResourceType) => boolean | "all" | "own";
  canDeleteAll: (resource: ResourceType) => boolean;
  canDeleteOwn: (resource: ResourceType) => boolean;
  isLoading: boolean;
  refetchPermissions: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(
  undefined,
);

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error("usePermissions must be used within a PermissionsProvider");
  }
  return context;
};

interface PermissionsProviderProps {
  children: ReactNode;
}

export const PermissionsProvider: React.FC<PermissionsProviderProps> = ({
  children,
}) => {
  const { user, isAuthenticated } = useAuth();
  const [permissions, setPermissions] = useState<ResourcePermission[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setPermissions([]);
      setIsLoading(false);
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
      const token = localStorage.getItem("token");

      if (!token) {
        setPermissions([]);
        setIsLoading(false);
        return;
      }

      const response = await fetch(`${apiUrl}/api/users/me/permissions`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        console.error("Failed to fetch permissions:", response.statusText);
        setPermissions([]);
        setIsLoading(false);
        return;
      }

      const data = await response.json();
      if (data.success) {
        setPermissions(data.data?.permissions || []);
      } else {
        setPermissions([]);
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
      setPermissions([]);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // Expose refetch function globally for impersonation
  useEffect(() => {
    if (isAuthenticated) {
      (window as any).__refetchPermissions = fetchPermissions;
    }
    return () => {
      delete (window as any).__refetchPermissions;
    };
  }, [isAuthenticated, fetchPermissions]);

  const hasPermission = useCallback(
    (
      resource: ResourceType,
      action: PermissionAction,
    ): boolean | "all" | "own" => {
      // System Administrators have all permissions
      if (user?.role === "System Administrator") {
        return "all";
      }

      // Check if user has permissions from custom role (backend returns custom role permissions if assigned)
      const resourcePermission = permissions.find(
        (p) => p.resource === resource,
      );

      // If permissions exist (from custom role), use those - custom role overrides Account Owner
      if (resourcePermission) {
        // Normalize legacy actions
        const normalizedAction =
          action === "view"
            ? "view_all"
            : action === "add"
              ? "add_all"
              : action === "edit"
                ? "edit_all"
                : action === "approve"
                  ? "approve_all"
                  : action === "delete"
                    ? "delete_all"
                    : action;

        // Check for exact match first
        if (resourcePermission.actions.includes(normalizedAction)) {
          if (normalizedAction.endsWith("_own")) {
            return "own";
          } else if (normalizedAction.endsWith("_all")) {
            return "all";
          } else {
            return true; // Non-scoped actions (approve, delete)
          }
        }

        // Check for _all variant when checking for _all (e.g., view_all should accept view_own)
        if (normalizedAction.endsWith("_all")) {
          const ownVariant = normalizedAction.replace(
            "_all",
            "_own",
          ) as PermissionAction;
          if (resourcePermission.actions.includes(ownVariant)) {
            return "own";
          }
        }

        // Check for _all variant when checking for _own (e.g., view_own should accept view_all)
        if (normalizedAction.endsWith("_own")) {
          const allVariant = normalizedAction.replace(
            "_own",
            "_all",
          ) as PermissionAction;
          if (resourcePermission.actions.includes(allVariant)) {
            return "all";
          }
        }

        return false;
      }

      return false;
    },
    [user?.role, permissions],
  );

  const canView = useCallback(
    (resource: ResourceType) => hasPermission(resource, "view"),
    [hasPermission],
  );
  const canViewAll = useCallback(
    (resource: ResourceType) => hasPermission(resource, "view_all") === "all",
    [hasPermission],
  );
  const canViewOwn = useCallback(
    (resource: ResourceType) => {
      const result = hasPermission(resource, "view_own");
      return result === "own" || result === "all";
    },
    [hasPermission],
  );

  const canAdd = useCallback(
    (resource: ResourceType) => hasPermission(resource, "add"),
    [hasPermission],
  );
  const canAddAll = useCallback(
    (resource: ResourceType) => hasPermission(resource, "add_all") === "all",
    [hasPermission],
  );
  const canAddOwn = useCallback(
    (resource: ResourceType) => {
      const result = hasPermission(resource, "add_own");
      return result === "own" || result === "all";
    },
    [hasPermission],
  );

  const canEdit = useCallback(
    (resource: ResourceType) => hasPermission(resource, "edit"),
    [hasPermission],
  );
  const canEditAll = useCallback(
    (resource: ResourceType) => hasPermission(resource, "edit_all") === "all",
    [hasPermission],
  );
  const canEditOwn = useCallback(
    (resource: ResourceType) => {
      const result = hasPermission(resource, "edit_own");
      return result === "own" || result === "all";
    },
    [hasPermission],
  );

  const canApprove = useCallback(
    (resource: ResourceType) => hasPermission(resource, "approve"),
    [hasPermission],
  );
  const canApproveAll = useCallback(
    (resource: ResourceType) =>
      hasPermission(resource, "approve_all") === "all",
    [hasPermission],
  );
  const canApproveOwn = useCallback(
    (resource: ResourceType) => {
      const result = hasPermission(resource, "approve_own");
      return result === "own" || result === "all";
    },
    [hasPermission],
  );

  const canDelete = useCallback(
    (resource: ResourceType) => hasPermission(resource, "delete"),
    [hasPermission],
  );
  const canDeleteAll = useCallback(
    (resource: ResourceType) => hasPermission(resource, "delete_all") === "all",
    [hasPermission],
  );
  const canDeleteOwn = useCallback(
    (resource: ResourceType) => {
      const result = hasPermission(resource, "delete_own");
      return result === "own" || result === "all";
    },
    [hasPermission],
  );

  const value: PermissionsContextType = {
    permissions,
    hasPermission,
    canView,
    canViewAll,
    canViewOwn,
    canAdd,
    canAddAll,
    canAddOwn,
    canEdit,
    canEditAll,
    canEditOwn,
    canApprove,
    canApproveAll,
    canApproveOwn,
    canDelete,
    canDeleteAll,
    canDeleteOwn,
    isLoading,
    refetchPermissions: fetchPermissions,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};
